/**
 * Operating-calendar editor (ADR-126 Decision 3).
 *
 * For the selected scope, authors the site operating calendar: recurring
 * weekly hours, dated closures/exceptions, and scheduled drill/training/
 * field-exercise events. Honors org→site inheritance — a site with no own
 * calendar shows the inherited org default read-only with a "create site
 * override" affordance; the org-default scope edits the baseline directly.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  HStack,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DataTable,
  EmptyState,
  StatusBadge,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { HiCalendarDays } from 'react-icons/hi2';
import { toaster } from '../ui/toaster';
import type { ConfigScope, ScopeRow } from './types';
import { scopeOf, ownKeysFrom } from './types';
import { useScopeRows } from './useScopeRows';
import { ScopeGroupTable } from './ScopeGroupTable';
import { useScopedCalendar } from './useScopedCalendar';
import {
  useOperatingCalendars,
  useCreateOperatingCalendar,
  useUpdateOperatingCalendar,
  useOperatingHours,
  useDeleteOperatingHours,
  useOperatingExceptions,
  useDeleteOperatingException,
  useScheduledSiteEvents,
  useDeleteScheduledSiteEvent,
  useResolvedSiteState,
} from '../../hooks/queries';
import {
  WEEKDAY_LABELS,
  EXCEPTION_KIND_LABELS,
  SITE_EVENT_TYPE_LABELS,
  type OperatingHours,
  type OperatingException,
  type ScheduledSiteEvent,
} from '../../services';
import { OperatingHoursDrawer } from './drawers/OperatingHoursDrawer';
import { OperatingExceptionDrawer } from './drawers/OperatingExceptionDrawer';
import { ScheduledEventDrawer } from './drawers/ScheduledEventDrawer';

/**
 * Operating Hours tab: a site-grouped table — "Organization default"
 * + one row per site — each expanding to that scope's operating calendar
 * (recurring hours, closures/exceptions, scheduled events), rather than gating
 * behind a scope-selector dropdown. Each scope is edited independently.
 */
export function OperatingCalendarEditor() {
  const { t } = useTranslation();
  const { data: calendars = [], isLoading: calendarsLoading } =
    useOperatingCalendars({});
  const ownKeys = useMemo(() => ownKeysFrom(calendars), [calendars]);
  const { rows, isLoading: rowsLoading, error } = useScopeRows(ownKeys);

  const calendarFor = (row: ScopeRow) =>
    calendars.find((c) =>
      row.kind === 'org'
        ? c.school == null && c.district === row.districtId
        : c.school === row.schoolId,
    );

  return (
    <ScopeGroupTable
      rows={rows}
      isLoading={calendarsLoading || rowsLoading}
      error={error}
      searchPlaceholder={t('whakahaere.searchSites', 'Search sites')}
      summaryColumn={{
        key: 'calendar',
        label: t('whakahaere.calendar', 'Calendar'),
        sortable: false,
        render: (row) => {
          const cal = calendarFor(row);
          return (
            <Text fontSize="sm" color="text.secondary">
              {cal
                ? cal.always_operating
                  ? t('whakahaere.twentyFourSeven', '24/7')
                  : cal.name || t('whakahaere.customHours', 'Custom hours')
                : '—'}
            </Text>
          );
        },
      }}
      renderExpandedRow={(row) => (
        <OperatingCalendarPanel scope={scopeOf(row)} />
      )}
      e2eTitle="operating-scopes"
    />
  );
}

interface OperatingCalendarPanelProps {
  scope: ConfigScope;
}

/** Trim `HH:MM:SS` → `HH:MM` for display. */
function fmtTime(t?: string | null): string {
  if (!t) return '—';
  return t.slice(0, 5);
}

function OperatingCalendarPanel({ scope }: OperatingCalendarPanelProps) {
  const { t } = useTranslation();
  const { calendar, isInherited, hasOwnCalendar, isLoading } =
    useScopedCalendar(scope);

  const createCalendar = useCreateOperatingCalendar();
  const updateCalendar = useUpdateOperatingCalendar();

  const calendarId = hasOwnCalendar ? calendar?.id : undefined;

  const { data: hours = [], isLoading: hoursLoading } =
    useOperatingHours(calendarId);
  const { data: exceptions = [], isLoading: exceptionsLoading } =
    useOperatingExceptions(calendarId);
  const { data: events = [], isLoading: eventsLoading } =
    useScheduledSiteEvents(calendarId);
  const { data: resolved } = useResolvedSiteState(
    scope.schoolId ?? undefined,
  );

  const deleteHours = useDeleteOperatingHours(calendarId ?? 0);
  const deleteException = useDeleteOperatingException(calendarId ?? 0);
  const deleteEvent = useDeleteScheduledSiteEvent(calendarId ?? 0);

  // Drawer state — one open editor at a time across the three collections.
  const [hoursEdit, setHoursEdit] = useState<{
    open: boolean;
    row: OperatingHours | null;
  }>({ open: false, row: null });
  const [exceptionEdit, setExceptionEdit] = useState<{
    open: boolean;
    row: OperatingException | null;
  }>({ open: false, row: null });
  const [eventEdit, setEventEdit] = useState<{
    open: boolean;
    row: ScheduledSiteEvent | null;
  }>({ open: false, row: null });

  const handleCreateCalendar = async () => {
    try {
      await createCalendar.mutateAsync({
        name: scope.schoolName
          ? `${scope.schoolName} hours`
          : `${scope.districtName} default`,
        district: scope.schoolId ? null : scope.districtId,
        school: scope.schoolId ?? null,
        term: null,
        always_operating: false,
      });
      toaster.create({
        title: scope.schoolId
          ? t('whakahaere.siteOverrideCreated', 'Site override created')
          : t('whakahaere.calendarCreated', 'Calendar created'),
        type: 'success',
      });
    } catch (err) {
      toaster.create({
        title: t('whakahaere.couldNotCreateCalendar', 'Could not create the calendar'),
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  };

  const handleToggleAlwaysOperating = async (checked: boolean) => {
    if (!calendar || !hasOwnCalendar) return;
    try {
      await updateCalendar.mutateAsync({
        id: calendar.id,
        data: { always_operating: checked },
      });
    } catch (err) {
      toaster.create({
        title: t('whakahaere.couldNotUpdateCalendar', 'Could not update the calendar'),
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  // No calendar at all for this scope, or a site inheriting the org default.
  if (!hasOwnCalendar) {
    return (
      <Box px={5} py={6}>
        {isInherited && calendar ? (
          <EmptyState
            icon={HiCalendarDays}
            title={t('whakahaere.inheritingOrgDefault', 'Inheriting the organization default')}
            description={t(
              'whakahaere.inheritingOrgDefaultDesc',
              '{{siteName}} currently uses the {{districtName}} default operating calendar. Create a site override to set hours specific to this site.',
              {
                siteName: scope.schoolName ?? t('whakahaere.thisSite', 'This site'),
                districtName: scope.districtName,
              },
            )}
            action={
              <ButtonComponent
                visual="primary"
                onClick={handleCreateCalendar}
                data-e2e="create-site-override-button"
              >
                {t('whakahaere.createSiteOverride', 'Create site override')}
              </ButtonComponent>
            }
            e2eTitle="inherited-calendar-empty-state"
          />
        ) : (
          <EmptyState
            icon={HiCalendarDays}
            title={t('whakahaere.noCalendarYet', 'No operating calendar yet')}
            description={
              scope.schoolId
                ? t(
                    'whakahaere.noSiteCalendarDesc',
                    '{{siteName}} has no operating calendar and its organization has no default. Create one to define operating hours.',
                    { siteName: scope.schoolName ?? t('whakahaere.thisSite', 'This site') },
                  )
                : t(
                    'whakahaere.noOrgCalendarDesc',
                    '{{districtName}} has no default operating calendar. Create one to define hours inherited by all its sites.',
                    { districtName: scope.districtName },
                  )
            }
            action={
              <ButtonComponent
                visual="primary"
                onClick={handleCreateCalendar}
                data-e2e="create-calendar-button"
              >
                {scope.schoolId
                  ? t('whakahaere.createSiteCalendar', 'Create site calendar')
                  : t('whakahaere.createOrgDefault', 'Create organization default')}
              </ButtonComponent>
            }
            e2eTitle="no-calendar-empty-state"
          />
        )}
      </Box>
    );
  }

  const hoursColumns: Column<OperatingHours>[] = [
    {
      key: 'weekday',
      label: t('whakahaere.columns.day', 'Day'),
      render: (row) => <Text>{WEEKDAY_LABELS[row.weekday]}</Text>,
    },
    {
      key: 'open_time',
      label: t('whakahaere.columns.opens', 'Opens'),
      render: (row) => <Text>{fmtTime(row.open_time)}</Text>,
    },
    {
      key: 'close_time',
      label: t('whakahaere.columns.closes', 'Closes'),
      render: (row) => <Text>{fmtTime(row.close_time)}</Text>,
    },
  ];

  const exceptionColumns: Column<OperatingException>[] = [
    { key: 'date', label: t('whakahaere.columns.date', 'Date'), render: (row) => <Text>{row.date}</Text> },
    {
      key: 'kind',
      label: t('whakahaere.columns.kind', 'Kind'),
      render: (row) => (
        <Text>{EXCEPTION_KIND_LABELS[row.kind ?? 'closed']}</Text>
      ),
    },
    {
      key: 'hours',
      label: t('whakahaere.columns.hours', 'Hours'),
      render: (row) =>
        row.kind === 'custom_hours' ? (
          <Text>
            {fmtTime(row.open_time)}–{fmtTime(row.close_time)}
          </Text>
        ) : (
          <Text color="text.secondary">—</Text>
        ),
    },
    {
      key: 'reason',
      label: t('whakahaere.columns.reason', 'Reason'),
      render: (row) => (
        <Text lineClamp={1}>{row.reason || '—'}</Text>
      ),
    },
  ];

  const eventColumns: Column<ScheduledSiteEvent>[] = [
    {
      key: 'event_type',
      label: t('whakahaere.columns.type', 'Type'),
      render: (row) => (
        <StatusBadge
          status={'info' as StatusType}
          label={SITE_EVENT_TYPE_LABELS[row.event_type ?? 'drill']}
        />
      ),
    },
    {
      key: 'name',
      label: t('whakahaere.columns.name', 'Name'),
      render: (row) => (
        <Text>{row.name || SITE_EVENT_TYPE_LABELS[row.event_type ?? 'drill']}</Text>
      ),
    },
    {
      key: 'start',
      label: t('whakahaere.columns.starts', 'Starts'),
      render: (row) => (
        <Text>{new Date(row.start).toLocaleString()}</Text>
      ),
    },
    {
      key: 'duration_minutes',
      label: t('whakahaere.columns.duration', 'Duration'),
      render: (row) => <Text>{row.duration_minutes} min</Text>,
    },
    {
      key: 'recurrence_rule',
      label: t('whakahaere.columns.repeats', 'Repeats'),
      render: (row) => (
        <Text color={row.recurrence_rule ? undefined : 'text.secondary'}>
          {row.recurrence_rule ? t('common.yes', 'Yes') : t('whakahaere.oneOff', 'One-off')}
        </Text>
      ),
    },
  ];

  return (
    <Box px={5} py={5}>
      <VStack align="stretch" gap={6}>
        {/* Calendar-level controls */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <HStack gap={3}>
            <Text fontWeight="600" fontSize="md" data-e2e="calendar-name">
              {calendar?.name || t('whakahaere.operatingCalendar', 'Operating calendar')}
            </Text>
            {resolved && (
              <Box data-e2e="resolved-state-badge">
                <StatusBadge
                  status={
                    (resolved.state === 'operating'
                      ? 'active'
                      : 'inactive') as StatusType
                  }
                  label={
                    resolved.state === 'operating'
                      ? t('whakahaere.operatingNow', 'Operating now')
                      : t('whakahaere.notOperatingNow', 'Not operating now')
                  }
                />
              </Box>
            )}
            {resolved?.in_scheduled_event && (
              <Box data-e2e="in-scheduled-event-badge">
                <StatusBadge status={'info' as StatusType} label={t('whakahaere.inScheduledEvent', 'In scheduled event')} />
              </Box>
            )}
          </HStack>
          <Switch.Root
            checked={!!calendar?.always_operating}
            onCheckedChange={(d) => handleToggleAlwaysOperating(d.checked)}
          >
            <Switch.HiddenInput data-e2e="always-operating-switch" />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Switch.Label>{t('whakahaere.alwaysOperatingLabel', '24/7 — always operating')}</Switch.Label>
          </Switch.Root>
        </Flex>

        {calendar?.always_operating && (
          <Text fontSize="sm" color="text.secondary" data-e2e="always-operating-text">
            {t('whakahaere.alwaysOperatingText', 'This site is always operating; weekly hours, exceptions and the term envelope are ignored. Scheduled events still apply.')}
          </Text>
        )}

        {/* Recurring hours */}
        <Box data-e2e="recurring-hours-section">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="600">{t('whakahaere.recurringHours', 'Recurring hours')}</Text>
            <ButtonComponent
              visual="secondary"
              onClick={() => setHoursEdit({ open: true, row: null })}
              disabled={!!calendar?.always_operating}
              data-e2e="add-hours-button"
            >
              {t('whakahaere.addHours', 'Add hours')}
            </ButtonComponent>
          </Flex>
          {hoursLoading ? (
            <Center py={8}>
              <Spinner color="button.primary" />
            </Center>
          ) : hours.length === 0 ? (
            <EmptyState
              icon={HiCalendarDays}
              title={t('whakahaere.noRecurringHours', 'No recurring hours')}
              description={t('whakahaere.noRecurringHoursDesc', 'Add weekly (day, open, close) windows to define when this site operates.')}
              e2eTitle="no-hours-empty-state"
            />
          ) : (
            <DataTable
              columns={hoursColumns}
              data={hours}
              keyExtractor={(row) => String(row.id)}
              onRowClick={(row) => setHoursEdit({ open: true, row })}
            />
          )}
        </Box>

        {/* Dated exceptions */}
        <Box data-e2e="closures-exceptions-section">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="600">{t('whakahaere.closuresExceptions', 'Closures & exceptions')}</Text>
            <ButtonComponent
              visual="secondary"
              onClick={() => setExceptionEdit({ open: true, row: null })}
              data-e2e="add-exception-button"
            >
              {t('whakahaere.addException', 'Add exception')}
            </ButtonComponent>
          </Flex>
          {exceptionsLoading ? (
            <Center py={8}>
              <Spinner color="button.primary" />
            </Center>
          ) : exceptions.length === 0 ? (
            <EmptyState
              icon={HiCalendarDays}
              title={t('whakahaere.noExceptions', 'No exceptions')}
              description={t('whakahaere.noExceptionsDesc', 'Add holidays, breaks, early-release days, or off-cycle open days.')}
              e2eTitle="no-exceptions-empty-state"
            />
          ) : (
            <DataTable
              columns={exceptionColumns}
              data={exceptions}
              keyExtractor={(row) => String(row.id)}
              onRowClick={(row) => setExceptionEdit({ open: true, row })}
            />
          )}
        </Box>

        {/* Scheduled events */}
        <Box data-e2e="scheduled-events-section">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="600">{t('whakahaere.scheduledDrillsTrainings', 'Scheduled drills & trainings')}</Text>
            <ButtonComponent
              visual="secondary"
              onClick={() => setEventEdit({ open: true, row: null })}
              data-e2e="add-event-button"
            >
              {t('whakahaere.addEvent', 'Add event')}
            </ButtonComponent>
          </Flex>
          {eventsLoading ? (
            <Center py={8}>
              <Spinner color="button.primary" />
            </Center>
          ) : events.length === 0 ? (
            <EmptyState
              icon={HiCalendarDays}
              title={t('whakahaere.noScheduledEvents', 'No scheduled events')}
              description={t('whakahaere.noScheduledEventsDesc', 'Schedule drills, trainings or field exercises on this calendar\'s timeline.')}
              e2eTitle="no-events-empty-state"
            />
          ) : (
            <DataTable
              columns={eventColumns}
              data={events}
              keyExtractor={(row) => String(row.id)}
              onRowClick={(row) => setEventEdit({ open: true, row })}
            />
          )}
        </Box>
      </VStack>

      {/* Drawers */}
      {calendarId != null && (
        <>
          <OperatingHoursDrawer
            calendarId={calendarId}
            isOpen={hoursEdit.open}
            row={hoursEdit.row}
            onClose={() => setHoursEdit({ open: false, row: null })}
            onDelete={(id) => {
              deleteHours.mutate(id);
              setHoursEdit({ open: false, row: null });
            }}
          />
          <OperatingExceptionDrawer
            calendarId={calendarId}
            isOpen={exceptionEdit.open}
            row={exceptionEdit.row}
            onClose={() => setExceptionEdit({ open: false, row: null })}
            onDelete={(id) => {
              deleteException.mutate(id);
              setExceptionEdit({ open: false, row: null });
            }}
          />
          <ScheduledEventDrawer
            calendarId={calendarId}
            isOpen={eventEdit.open}
            row={eventEdit.row}
            onClose={() => setEventEdit({ open: false, row: null })}
            onDelete={(id) => {
              deleteEvent.mutate(id);
              setEventEdit({ open: false, row: null });
            }}
          />
        </>
      )}
    </Box>
  );
}
